import { ChevronDown, Plus, Save, Trash2, X } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  BOOKING_TYPES,
  EXPENSE_CATEGORIES,
  PAYMENT_MODES,
  TRIP_STATUSES,
  VEHICLE_SOURCES,
  type BookingType,
  type ExpenseCategory,
  type ExpenseFormRow,
  type ExpenseRecord,
  type PaymentFormRow,
  type PaymentMode,
  type PaymentRecord,
  type Trip,
  type TripFormData,
  type TripSavePayload,
  type TripStatus,
  type VehicleSource,
} from "./types";
import type { TripFieldSuggestions } from "./selectors";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";
import { formatCurrency, formatPercent } from "../shared/currency";
import {
  createExpenseFormRow,
  createEmptyTripForm,
  createPaymentFormRow,
  formToTripSavePayload,
  getFormPreview,
  tripToForm,
} from "./tripForm.model";

type TripFormDrawerProps = {
  trip?: Trip | null;
  initialForm?: TripFormData | null;
  payments?: PaymentRecord[];
  expenses?: ExpenseRecord[];
  suggestions?: TripFieldSuggestions;
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: TripSavePayload) => void;
};

export function TripFormDrawer({
  trip,
  initialForm,
  payments = [],
  expenses = [],
  suggestions,
  isOpen,
  onClose,
  onSave,
}: TripFormDrawerProps) {
  const [form, setForm] = useState<TripFormData>(createEmptyTripForm);
  const dialogRef = useRef<HTMLElement>(null);
  const formRef = useRef(form);
  const initialSnapshotRef = useRef("");
  const preview = useMemo(() => getFormPreview(form), [form]);

  useEffect(() => {
    if (!isOpen) return;
    const nextForm = initialForm ?? (trip ? tripToForm(trip, payments, expenses) : createEmptyTripForm());
    setForm(nextForm);
    formRef.current = nextForm;
    initialSnapshotRef.current = JSON.stringify(nextForm);
  }, [initialForm, isOpen, trip, payments, expenses]);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
    }, 0);
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const isDirty = JSON.stringify(formRef.current) !== initialSnapshotRef.current;
      if (isDirty && !window.confirm("Discard the unsaved trip changes?")) return;
      onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSave(formToTripSavePayload(form, trip?.id));
  };

  const requestClose = () => {
    const isDirty = JSON.stringify(form) !== initialSnapshotRef.current;
    if (isDirty && !window.confirm("Discard the unsaved trip changes?")) return;
    onClose();
  };

  const trapDialogFocus = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary'
      ) ?? []
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const updatePayment = <K extends keyof PaymentFormRow>(
    id: string,
    key: K,
    value: PaymentFormRow[K]
  ) => {
    setForm((current) => ({
      ...current,
      payments: current.payments.map((payment) =>
        payment.id === id ? { ...payment, [key]: value } : payment
      ),
    }));
  };

  const updateExpense = <K extends keyof ExpenseFormRow>(
    id: string,
    key: K,
    value: ExpenseFormRow[K]
  ) => {
    setForm((current) => ({
      ...current,
      expenses: current.expenses.map((expense) =>
        expense.id === id ? { ...expense, [key]: value } : expense
      ),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#02040C]/80 p-3 backdrop-blur-sm sm:p-5">
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={trip ? "Edit trip" : "New trip"}
        onKeyDown={trapDialogFocus}
        className="orbit-card orbit-trip-dialog flex w-full max-w-6xl flex-col overflow-hidden"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgba(255,255,255,0.07)] px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {trip ? "Edit trip" : "New trip"}
            </p>
            <h2 className="orbit-display truncate text-xl font-bold text-slate-950">
              {form.vehicleNo || "Trip entry"}
            </h2>
            <p className="mt-0.5 hidden text-xs text-slate-500 sm:block">
              {trip?.tripNo || "Trip number will be generated automatically on save."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              form="trip-form"
              className="orbit-button-primary orbit-desktop-save px-4 py-2 text-sm"
            >
              <Save size={16} />
              Save
            </button>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close trip form"
              className="orbit-tab hidden h-10 w-10 place-items-center rounded-xl transition lg:grid"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form
          id="trip-form"
          onSubmit={handleSubmit}
          className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_auto] overflow-hidden lg:grid-cols-[minmax(0,1fr)_300px] lg:grid-rows-1"
        >
          <div className="space-y-3 overflow-y-auto p-3 sm:p-5">
            <FormSection title="Trip essentials" defaultOpen>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
                <Field label="Date" name="date" type="date" value={form.date} onChange={handleChange} required />
                <SelectField<TripStatus>
                  label="Trip Status"
                  value={form.status}
                  options={TRIP_STATUSES}
                  onChange={(status) =>
                    setForm((current) => ({ ...current, status }))
                  }
                />
                <Field
                  label="Vehicle No"
                  name="vehicleNo"
                  value={form.vehicleNo}
                  onChange={handleChange}
                  suggestions={suggestions?.vehicleNo}
                  required
                />
                <Field
                  label="Party Name"
                  name="partyName"
                  value={form.partyName}
                  onChange={handleChange}
                  suggestions={suggestions?.partyName}
                  required
                />
                <Field label="Hire Amount" name="hireAmount" value={form.hireAmount} onChange={handleChange} type="number" />
                <Field
                  label="Origin"
                  name="origin"
                  value={form.origin}
                  onChange={handleChange}
                  suggestions={suggestions?.origin}
                  required
                />
                <Field
                  label="Destination"
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  suggestions={suggestions?.destination}
                  required
                />
              </div>
            </FormSection>

            <FormSection title="Booking and driver details">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
                <SelectField<BookingType>
                  label="Booking Type"
                  value={form.bookingType}
                  options={BOOKING_TYPES}
                  onChange={(bookingType) =>
                    setForm((current) => ({ ...current, bookingType }))
                  }
                />
                <SelectField<VehicleSource>
                  label="Vehicle Source"
                  value={form.vehicleSource}
                  options={VEHICLE_SOURCES}
                  onChange={(vehicleSource) =>
                    setForm((current) => ({ ...current, vehicleSource }))
                  }
                />
                {form.bookingType !== "Direct" ? (
                  <Field
                    label="Broker Name"
                    name="brokerName"
                    value={form.brokerName}
                    onChange={handleChange}
                    suggestions={suggestions?.brokerName}
                    required
                  />
                ) : null}
                <Field
                  label="Driver Name"
                  name="driverName"
                  value={form.driverName}
                  onChange={handleChange}
                  suggestions={suggestions?.driverName}
                />
                <Field label="Driver Mobile" name="driverMobile" value={form.driverMobile} onChange={handleChange} />
              </div>
            </FormSection>

            <FormSection title="Payments received" count={form.payments.length}>
              <div className="space-y-3">
                {form.payments.map((payment) => (
                  <PaymentRow
                    key={payment.id}
                    payment={payment}
                    onChange={(key, value) => updatePayment(payment.id, key, value)}
                    onRemove={() =>
                      setForm((current) => ({
                        ...current,
                        payments: current.payments.filter((item) => item.id !== payment.id),
                      }))
                    }
                  />
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      payments: [...current.payments, createPaymentFormRow(current.date)],
                    }))
                  }
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
                >
                  <Plus size={16} />
                  Add Payment
                </button>
              </div>
            </FormSection>

            <FormSection title="Direct expenses" count={form.expenses.length}>
              <div className="space-y-3">
                {form.expenses.map((expense) => (
                  <ExpenseRow
                    key={expense.id}
                    expense={expense}
                    onChange={(key, value) => updateExpense(expense.id, key, value)}
                    onRemove={() =>
                      setForm((current) => ({
                        ...current,
                        expenses: current.expenses.filter((item) => item.id !== expense.id),
                      }))
                    }
                  />
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      expenses: [...current.expenses, createExpenseFormRow(current.date)],
                    }))
                  }
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"
                >
                  <Plus size={16} />
                  Add Expense
                </button>
              </div>
            </FormSection>

            <FormSection title="Notes" count={form.notes.trim() ? 1 : 0}>
              <TextAreaField
                label="Trip Notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
              />
            </FormSection>
          </div>

          <aside className="hidden border-l border-slate-200 bg-slate-50 p-4 lg:block">
            <div className="sticky top-4 space-y-3">
              <section className="orbit-card p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Live financial preview
                </p>
                <div className="mt-3 space-y-2.5">
                  <PreviewLine label="Total expense" value={formatCurrency(preview.totalExpense)} />
                  <PreviewLine label="Outstanding" value={formatCurrency(preview.outstanding)} />
                  <PreviewLine label="Operating profit" value={formatCurrency(preview.operatingProfit)} />
                  <PreviewLine label="Margin" value={formatPercent(preview.marginPct)} />
                </div>
              </section>
            </div>
          </aside>

          <div className="orbit-form-mobile-footer border-t border-slate-200 bg-slate-50 p-3 lg:hidden">
            <div className="mb-2 grid grid-cols-3 gap-2 text-center text-xs">
              <div>
                <p className="text-slate-500">Expense</p>
                <p className="font-bold text-rose-700">{formatCurrency(preview.totalExpense)}</p>
              </div>
              <div>
                <p className="text-slate-500">Due</p>
                <p className="font-bold text-amber-700">{formatCurrency(preview.outstanding)}</p>
              </div>
              <div>
                <p className="text-slate-500">Profit</p>
                <p className="font-bold text-emerald-700">{formatCurrency(preview.operatingProfit)}</p>
              </div>
            </div>
            <div className="grid grid-cols-[0.8fr_1.2fr] gap-2">
              <button
                type="button"
                onClick={requestClose}
                className="orbit-tab min-h-11 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="orbit-button-primary min-h-11 px-4 py-2 text-sm"
              >
                <Save size={16} />
                Save Trip
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

type FieldProps = {
  label: string;
  name: keyof TripFormData;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  suggestions?: string[];
  type?: string;
  required?: boolean;
};

function Field({
  label,
  name,
  value,
  onChange,
  suggestions,
  type = "text",
  required,
}: FieldProps) {
  const listId = suggestions?.length ? `${String(name)}-suggestions` : undefined;

  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <input
        name={name}
        type={type}
        list={listId}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-1 w-full rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      {listId ? (
        <datalist id={listId}>
          {suggestions?.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

type SelectFieldProps<T extends string> = {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
};

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: SelectFieldProps<T>) {
  const selectOptions: SelectOption<T>[] = options.map((option) => ({
    value: option,
    label: option,
  }));

  return (
    <div className="min-w-0">
      <p className="text-xs font-bold uppercase text-slate-500">
        {label}
      </p>
      <CompactSelect
        ariaLabel={label}
        value={value}
        options={selectOptions}
        onChange={onChange}
        className="mt-1"
        buttonClassName="h-10 min-h-10"
      />
    </div>
  );
}

type TextAreaFieldProps = {
  label: string;
  name: keyof TripFormData;
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  rows?: number;
};

function TextAreaField({
  label,
  name,
  value,
  onChange,
  className = "",
  rows = 3,
}: TextAreaFieldProps) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        className="mt-1 w-full rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
    </label>
  );
}

function PaymentRow({
  payment,
  onChange,
  onRemove,
}: {
  payment: PaymentFormRow;
  onChange: <K extends keyof PaymentFormRow>(key: K, value: PaymentFormRow[K]) => void;
  onRemove: () => void;
}) {
  return (
    <div className="orbit-raised grid grid-cols-2 gap-2 p-2.5 xl:grid-cols-[130px_minmax(110px,1fr)_140px_minmax(150px,1.4fr)_auto] xl:p-2">
      <input
        type="date"
        value={payment.date}
        onChange={(event) => onChange("date", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <input
        type="number"
        placeholder="Amount"
        value={payment.amount}
        onChange={(event) => onChange("amount", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <CompactSelect<PaymentMode>
        ariaLabel="Payment mode"
        value={payment.mode}
        options={PAYMENT_MODES.map((mode) => ({ value: mode, label: mode }))}
        onChange={(mode) => onChange("mode", mode)}
        buttonClassName="h-10 min-h-10"
      />
      <input
        type="text"
        placeholder="Note"
        value={payment.note}
        onChange={(event) => onChange("note", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove payment"
        className="col-span-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 xl:col-span-1 xl:w-11 xl:px-0"
      >
        <Trash2 size={16} />
        <span className="xl:hidden">Remove Payment</span>
      </button>
    </div>
  );
}

function ExpenseRow({
  expense,
  onChange,
  onRemove,
}: {
  expense: ExpenseFormRow;
  onChange: <K extends keyof ExpenseFormRow>(key: K, value: ExpenseFormRow[K]) => void;
  onRemove: () => void;
}) {
  return (
    <div className="orbit-raised grid grid-cols-2 gap-2 p-2.5 xl:grid-cols-[130px_150px_minmax(110px,1fr)_minmax(150px,1.4fr)_auto] xl:p-2">
      <input
        type="date"
        value={expense.date}
        onChange={(event) => onChange("date", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <CompactSelect<ExpenseCategory>
        ariaLabel="Expense category"
        value={expense.category}
        options={EXPENSE_CATEGORIES.map((category) => ({
          value: category,
          label: category,
        }))}
        onChange={(category) => onChange("category", category)}
        buttonClassName="h-10 min-h-10"
      />
      <input
        type="number"
        placeholder="Amount"
        value={expense.amount}
        onChange={(event) => onChange("amount", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <input
        type="text"
        placeholder="Note"
        value={expense.note}
        onChange={(event) => onChange("note", event.target.value)}
        className="rounded-xl border px-3 py-1.5 text-sm outline-none"
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove expense"
        className="col-span-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 xl:col-span-1 xl:w-11 xl:px-0"
      >
        <Trash2 size={16} />
        <span className="xl:hidden">Remove Expense</span>
      </button>
    </div>
  );
}

function FormSection({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);

  return (
    <details
      className="orbit-form-section"
      open={isExpanded}
      onToggle={(event) => setIsExpanded(event.currentTarget.open)}
    >
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-sm font-bold text-slate-950">
        <span className="inline-flex items-center gap-2">
          {title}
          {typeof count === "number" && count > 0 ? (
            <span className="orbit-tone-blue rounded-full px-2 py-0.5 text-xs">{count}</span>
          ) : null}
        </span>
        <ChevronDown className="orbit-form-chevron shrink-0 text-slate-500" size={17} />
      </summary>
      <div className="orbit-form-section-body px-3 pb-3 pt-1">{children}</div>
    </details>
  );
}

function PreviewLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}
